import { useState, useEffect } from "react";
import { Link, useSearchParams, useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import axios from "axios";
import "../../i18n";
import { useTranslation } from "react-i18next";
import config from "../../../config.json";
import { LOGOUT_ACTION } from "../../store/actions/AuthActions";
import { endTabScopedAuthSession } from "../../utils/authToken";
import LoginLanguageSwitcher from "../components/LoginLanguageSwitcher";
import PasswordStrengthMeter from "../components/PasswordStrengthMeter";
import logo from "../../assets/images/logo5.png";
import authDiningBg from "../../assets/images/auth/auth-dining-bg.jpg";
import "../../assets/css/forest-luxe-auth.css";

const PASSWORD_RESET_LOGOUT_EVENT = "restaurantPasswordResetLogout";

const getHashToken = () => {
  const hash = window.location.hash || "";
  const queryIndex = hash.indexOf("?");
  if (queryIndex === -1) return null;
  return new URLSearchParams(hash.slice(queryIndex + 1)).get("token");
};

const ResetPassword = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const { token: pathToken } = useParams();
  const token = searchParams.get("token") || pathToken || getHashToken();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const navigate = useNavigate();
  const dispatch = useDispatch();

  useEffect(() => {
    if (!token) {
      setError(t("resetPasswordInvalidToken"));
    }
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!password || !confirmPassword) {
      setError(t("resetPasswordFillFields"));
      return;
    }

    if (password.length < 6) {
      setError(t("resetPasswordMinLength"));
      return;
    }

    if (password !== confirmPassword) {
      setError(t("resetPasswordMismatch"));
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post(`${config.dev}/auth/reset-password`, {
        token,
        password,
        confirmPassword,
      });

      setSuccess(res.data.message || t("resetPasswordSuccess"));
      // Clear any existing session, then wait so the success message is visible
      // before sending the user to login.
      endTabScopedAuthSession();
      [
        "tokenLogin",
        "email",
        "userDetails",
        "restaurantId",
        "restaurantCurrency",
        "restaurantSecondaryCurrency",
        "userRole",
        "franchiseFK",
        "isOwner",
        "userPermissions",
      ].forEach((key) => localStorage.removeItem(key));
      localStorage.setItem(PASSWORD_RESET_LOGOUT_EVENT, String(Date.now()));
      dispatch({ type: LOGOUT_ACTION });
      setTimeout(() => {
        navigate("/login", { replace: true });
      }, 1500);
    } catch (err) {
      const msg = err.response?.data?.message || t("resetPasswordFailed");
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fl-auth">
      <div className="fl-auth__scene" aria-hidden="true">
        <img
          className="fl-auth__scene-photo"
          src={authDiningBg}
          alt=""
          decoding="async"
          fetchPriority="high"
        />
      </div>
      <div className="fl-auth__book" role="presentation">
        <div className="fl-auth__book-cover" aria-hidden="true" />
        <div className="fl-auth__book-pages">
          <span className="fl-auth__book-spine" aria-hidden="true" />

          <section className="fl-auth__welcome">
            <div className="fl-auth__lang">
              <LoginLanguageSwitcher />
            </div>

            <div className="fl-auth__welcome-aura" aria-hidden="true" />
            <span className="fl-auth__welcome-watermark" aria-hidden="true">
              C
            </span>

            <span className="fl-auth__filigree fl-auth__filigree--tl" aria-hidden="true" />
            <span className="fl-auth__filigree fl-auth__filigree--tr" aria-hidden="true" />
            <span className="fl-auth__filigree fl-auth__filigree--bl" aria-hidden="true" />
            <span className="fl-auth__filigree fl-auth__filigree--br" aria-hidden="true" />

            <div className="fl-auth__welcome-plate">
              <div className="fl-auth__welcome-crest">
                <img src={logo} alt="ClicToEat" className="fl-auth__logo" />
              </div>

              <div className="fl-auth__welcome-stars" aria-hidden="true">
                <span />
                <span />
                <span />
              </div>

              <p className="fl-auth__welcome-kicker">
                {t("brandChipRestaurantBackoffice")}
              </p>

              <h1 className="fl-auth__welcome-title">
                {t("resetPasswordHeroTitle")}
              </h1>

              <p className="fl-auth__welcome-text">
                {t("resetPasswordHeroDescription")}
              </p>

              <div className="fl-auth__welcome-seal">
                <span className="fl-auth__welcome-seal-ring" aria-hidden="true" />
                <span className="fl-auth__welcome-seal-text">ClicToEat</span>
              </div>
            </div>
          </section>

          <section className="fl-auth__panel">
            <div className="fl-auth__leaf fl-auth__leaf--static">
              <div className="fl-auth__card">
                <span className="fl-auth__chip">
                  {t("brandChipRestaurantBackoffice")}
                </span>
                <h2 className="fl-auth__title">{t("resetPasswordPageTitle")}</h2>
                <p className="fl-auth__subtitle">
                  {t("resetPasswordPageSubtitle")}
                </p>

                {error ? (
                  <div className="fl-auth__alert fl-auth__alert--danger">
                    {error}
                  </div>
                ) : null}
                {success ? (
                  <div className="fl-auth__alert fl-auth__alert--success">
                    {success}
                  </div>
                ) : null}

                {!success ? (
                  <form onSubmit={handleSubmit}>
                    <div className="fl-auth__field">
                      <label className="fl-auth__label" htmlFor="password">
                        {t("resetPasswordNewLabel")}{" "}
                        <span className="fl-auth__required">*</span>
                      </label>
                      <div className="fl-auth__input-wrap">
                        <input
                          type={showPassword ? "text" : "password"}
                          id="password"
                          className="fl-auth__input fl-auth__input--password"
                          placeholder={t("resetPasswordNewPlaceholder")}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          required
                          minLength={6}
                          disabled={!token || loading}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className={`fl-auth__eye${
                            showPassword ? " is-active" : ""
                          }`}
                          onClick={() => setShowPassword(!showPassword)}
                          aria-label="Toggle password visibility"
                        >
                          <i className="fa fa-eye-slash" />
                          <i className="fa fa-eye" />
                        </button>
                      </div>
                      <PasswordStrengthMeter
                        password={password}
                        labels={{
                          weak: t("passwordStrengthWeak"),
                          medium: t("passwordStrengthMedium"),
                          strong: t("passwordStrengthStrong"),
                        }}
                      />
                    </div>

                    <div className="fl-auth__field">
                      <label className="fl-auth__label" htmlFor="confirmPassword">
                        {t("resetPasswordConfirmLabel")}{" "}
                        <span className="fl-auth__required">*</span>
                      </label>
                      <div className="fl-auth__input-wrap">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          id="confirmPassword"
                          className="fl-auth__input fl-auth__input--password"
                          placeholder={t("resetPasswordConfirmPlaceholder")}
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          required
                          minLength={6}
                          disabled={!token || loading}
                          autoComplete="new-password"
                        />
                        <button
                          type="button"
                          className={`fl-auth__eye${
                            showConfirmPassword ? " is-active" : ""
                          }`}
                          onClick={() =>
                            setShowConfirmPassword(!showConfirmPassword)
                          }
                          aria-label="Toggle confirm password visibility"
                        >
                          <i className="fa fa-eye-slash" />
                          <i className="fa fa-eye" />
                        </button>
                      </div>
                    </div>

                    <p className="fl-auth__hint">{t("resetPasswordHint")}</p>

                    <button
                      type="submit"
                      className="fl-auth__submit"
                      disabled={!token || loading}
                    >
                      {loading
                        ? t("resetPasswordSubmitting")
                        : t("resetPasswordSubmit")}
                    </button>

                    <Link
                      to="/login"
                      className="fl-auth__link fl-auth__footer-link"
                    >
                      {t("resetPasswordBackToLogin")}
                    </Link>
                  </form>
                ) : null}
              </div>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
